/**
 * Console sign-in through NU Core SSO (#590) — the browser half.
 *
 * The PKCE verifier, state and code exchange live on the console API
 * (src/sso/routes.ts, app-kit helpers): this module only NAVIGATES to
 * `/api/v1/auth/login`, hands the `code` + `state` from `/auth/callback` back
 * to `/api/v1/auth/callback`, and keeps the resulting access token in the ONE
 * browser slot (tokenStore AUTH_TOKEN_KEY, sessionStorage — #559/#598). No
 * client secret, no refresh token, no verifier in browser JavaScript.
 *
 * Logout: the local clear is the guarantee; the API's POST /auth/logout ends
 * the NU Core session best-effort (core has no RP-initiated redirect logout).
 */
import {
  API_BASE_URL,
  ApiError,
  apiClient,
  createApiClient,
} from "../apiClient";
import { invalidateLaneAToken } from "./laneAToken";
import {
  AUTH_TOKEN_KEY,
  AUTH_USER_KEY,
  RPC_TOKEN_KEY,
  clearAuthValue,
  getAuthValue,
  setAuthValue,
} from "../tokenStore";
import { navigateTo } from "../../utils/navigation";
import type { CanonicalRole } from "../../rbac/roles";

/** The principal the API returns (src/sso/routes.ts publicUser). */
export interface SessionUser {
  id: string;
  email: string;
  name: string | null;
  role: CanonicalRole;
  roles: CanonicalRole[];
  tenantId: string;
  mfaVerified: boolean;
}

export interface CompletedLogin {
  user: SessionUser;
  returnTo: string;
}

/**
 * The callback must never be retried: the code is single-use and the API
 * clears the transaction cookie on the first attempt. No token is attached
 * and a 401 here must not trigger the global "session expired" redirect.
 */
const callbackClient = createApiClient({
  baseUrl: API_BASE_URL,
  tokenStore: null,
  onUnauthorized: null,
  maxRetries: 0,
});

/** Same-origin path only (mirrors the API's open-redirect guard). */
export function safeReturnTo(value: unknown): string {
  if (typeof value !== "string") return "/";
  if (!value.startsWith("/") || value.startsWith("//")) return "/";
  if (/[\\\r\n\t]/.test(value) || value.includes("://")) return "/";
  if (value.startsWith("/auth/callback") || value.startsWith("/login")) {
    return "/";
  }
  return value;
}

export function loginUrl(returnTo: string = "/"): string {
  const base = API_BASE_URL.replace(/\/+$/, "");
  return `${base}/auth/login?returnTo=${encodeURIComponent(safeReturnTo(returnTo))}`;
}

/** Start the Authorization Code + PKCE round-trip (full-page navigation). */
export function startLogin(returnTo: string = "/"): void {
  navigateTo(loginUrl(returnTo));
}

export interface SsoAvailability {
  enabled: boolean;
}

/** Whether the API can run the sign-in flow at all (it says why it cannot). */
export async function fetchSsoAvailability(): Promise<SsoAvailability> {
  try {
    const body = (await callbackClient.get("/auth/config")) as {
      data?: { enabled?: boolean };
    };
    return { enabled: body?.data?.enabled === true };
  } catch {
    return { enabled: false };
  }
}

const CALLBACK_MESSAGES: Record<string, string> = {
  invalid_callback:
    "This sign-in link has expired or was already used. Please sign in again.",
  invalid_state:
    "This sign-in attempt could not be verified. Please sign in again.",
  token_exchange_failed: "NU GUI sign-in did not complete. Please try again.",
  tenant_unmapped:
    "Your organisation is not set up in NU DATA yet. Contact your NU GUI administrator.",
  no_canonical_role:
    "Your NU GUI account has no NU DATA role. Contact your administrator.",
  user_inactive: "Your NU DATA account is deactivated.",
  email_conflict:
    "Your email is already linked to another NU DATA account. An administrator must resolve this.",
  sso_not_configured: "Sign-in is not configured for this console yet.",
  sso_error: "NU GUI sign-in was cancelled or refused. Please try again.",
};

export function callbackErrorMessage(code: string | null | undefined): string {
  return (
    (code && CALLBACK_MESSAGES[code]) || "Sign-in failed. Please try again."
  );
}

/** Store the session in the one tokenStore slot pair. */
function storeSession(accessToken: string, user: SessionUser): void {
  // A new console session invalidates any Lane A token minted for an old one.
  clearAuthValue(RPC_TOKEN_KEY);
  setAuthValue(AUTH_TOKEN_KEY, accessToken);
  setAuthValue(AUTH_USER_KEY, JSON.stringify(user));
}

/**
 * Finish sign-in: exchange `code` + `state` through the API. Throws an Error
 * whose `code` property is the API's problem code on refusal.
 */
export async function completeLogin(
  code: string,
  state: string,
): Promise<CompletedLogin> {
  let body: {
    data?: { accessToken?: string; returnTo?: string; user?: SessionUser };
  };
  try {
    body = (await callbackClient.post("/auth/callback", {
      body: { code, state },
    })) as typeof body;
  } catch (err) {
    const apiCode =
      err instanceof ApiError
        ? ((err.body as { code?: string } | null)?.code ?? err.code)
        : null;
    const e = new Error(callbackErrorMessage(apiCode)) as Error & {
      code?: string | null;
    };
    e.code = apiCode;
    throw e;
  }
  const accessToken = body?.data?.accessToken;
  const user = body?.data?.user;
  if (typeof accessToken !== "string" || !user?.id || !user.role) {
    throw new Error(callbackErrorMessage(null));
  }
  storeSession(accessToken, user);
  return { user, returnTo: safeReturnTo(body?.data?.returnTo) };
}

/** Re-read the principal for the stored token (role/tenant/deactivation). */
export async function refreshSessionUser(): Promise<SessionUser | null> {
  if (!getAuthValue(AUTH_TOKEN_KEY)) return null;
  try {
    const body = (await apiClient.get("/auth/session")) as {
      data?: { user?: SessionUser };
    };
    const user = body?.data?.user;
    if (!user?.id) return null;
    setAuthValue(AUTH_USER_KEY, JSON.stringify(user));
    return user;
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      clearSession();
    }
    return null;
  }
}

/** Drop every browser-held credential (console + Lane A). */
export function clearSession(): void {
  clearAuthValue(AUTH_TOKEN_KEY);
  clearAuthValue(AUTH_USER_KEY);
  // Also orphans an in-flight broker exchange so it cannot re-store (#972).
  invalidateLaneAToken();
}

/**
 * Sign out. The API call is STARTED first (apiClient reads the bearer
 * synchronously) but not awaited, so an unreachable backend can never hold
 * the UI signed in; the local clear is the guarantee.
 */
export function endSession(): void {
  if (getAuthValue(AUTH_TOKEN_KEY)) {
    void apiClient.post("/auth/logout").catch(() => {
      // NU Core session not ended; the local clear below still stands.
    });
  }
  clearSession();
}
